// Shared types + show-if evaluator for the Personal Rx questionnaire.
// Mirrors the legacy shape from src/data/personalRxQuestions.ts but is
// driven by the DB (table: personal_rx_questions).

export type Answers = Record<string, string | string[]>;

export type RxQuestionType = 'single' | 'multi' | 'text';

export type RuleLeaf = {
  key: string;
  op: 'equals' | 'not_equals' | 'includes';
  value: string;
};
export type RuleGroup = {
  op: 'and' | 'or';
  rules: ShowIfRule[];
};
export type ShowIfRule = RuleLeaf | RuleGroup;

export type RxQuestionRow = {
  id: string;
  key: string;
  label: string;
  hint: string | null;
  type: RxQuestionType;
  options: string[];
  required: boolean;
  placeholder: string | null;
  position: number;
  show_if: ShowIfRule | null;
  is_active: boolean;
  editor_x: number | null;
  editor_y: number | null;
};

export type RxQuestion = {
  /** Stable slug used as the answer-map key. Maps to DB `key`. */
  id: string;
  /** UUID row id in `personal_rx_questions`. */
  rowId: string;
  label: string;
  hint?: string;
  type: RxQuestionType;
  options: string[];
  required: boolean;
  placeholder?: string;
  position: number;
  showIf: ShowIfRule | null;
};

export function rowToQuestion(r: RxQuestionRow): RxQuestion {
  return {
    id: r.key,
    rowId: r.id,
    label: r.label,
    hint: r.hint ?? undefined,
    type: r.type,
    options: Array.isArray(r.options) ? r.options : [],
    required: r.required,
    placeholder: r.placeholder ?? undefined,
    position: r.position,
    showIf: r.show_if ?? null,
  };
}

// Evaluate a show-if rule against the answers map.
// `equals` and `includes` both treat array answers as "contains value" —
// this matches the legacy `eq()` helper in personalRxQuestions.ts.
export function evalShowIf(rule: ShowIfRule | null | undefined, a: Answers): boolean {
  if (!rule) return true;
  if ('rules' in rule) {
    if (rule.op === 'and') return rule.rules.every((r) => evalShowIf(r, a));
    if (rule.op === 'or') return rule.rules.some((r) => evalShowIf(r, a));
    return true;
  }
  const v = a[rule.key];
  const contains = Array.isArray(v) ? v.includes(rule.value) : v === rule.value;
  if (rule.op === 'equals' || rule.op === 'includes') return contains;
  if (rule.op === 'not_equals') return !contains;
  return true;
}

export const getVisibleQuestions = (qs: RxQuestion[], a: Answers): RxQuestion[] =>
  qs.filter((q) => evalShowIf(q.showIf, a));

// Best-effort section label for a question, used by the wizard for headers.
export const CATEGORIES = {
  HAIR: 'שיער וקרקפת',
  FACE: 'עור הפנים',
  NAILS: 'ציפורניים, ידיים ורגליים',
} as const;

const CAT_BY_PROBLEM_KEY: Record<string, string> = {
  main_problem: CATEGORIES.HAIR,
  face_problem: CATEGORIES.FACE,
  nails_problem: CATEGORIES.NAILS,
};

function collectKeys(rule: ShowIfRule | null | undefined, into: Set<string>) {
  if (!rule) return;
  if ('rules' in rule) rule.rules.forEach((r) => collectKeys(r, into));
  else into.add(rule.key);
}

export const getSectionFromRule = (rule: ShowIfRule | null | undefined): string | null => {
  if (!rule) return 'שאלות כלליות';
  const keys = new Set<string>();
  collectKeys(rule, keys);
  for (const [pk, cat] of Object.entries(CAT_BY_PROBLEM_KEY)) {
    if (keys.has(pk)) return cat;
  }
  if (keys.has('category')) {
    const found = findCategoryValue(rule);
    if (found) return found;
  }
  return null;
};

export const getQuestionSection = (q: RxQuestion): string | null =>
  getSectionFromRule(q.showIf);

function findCategoryValue(rule: ShowIfRule | null | undefined): string | null {
  if (!rule) return null;
  if ('rules' in rule) {
    for (const r of rule.rules) {
      const f = findCategoryValue(r);
      if (f) return f;
    }
    return null;
  }
  if (rule.key === 'category') return rule.value;
  return null;
}
